import EditButton from '../../../../components/EditButton';
import './CategoryItem.css';
import { useRef, useEffect,useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../../../authContext';

function CategoryItem({ categoryName, categoryDescription, categoryId, categoryPosition, isNew = false, onSave, onCancel, onDelete, assistantId, fetchCategories, setCategories, showUp = true, showDown = true, onMoveUp, onMoveDown }) {
    const { t } = useTranslation(), { t: tCommon } = useTranslation('common');
    const {accessToken} = useContext(AuthContext);
    const [isEditing, setIsEditing] = useState(isNew); // Começa em edição se for novo
    const [isRestoringPreviousDescription, setIsRestoringPreviousDescription] = useState(false);
    const [editedDescription, setEditedDescription] = useState(categoryDescription);
    const [editedCategoryName, setEditedCategoryName] = useState(categoryName);
    const textareaRef = useRef(null);

    const handleSaveClick = async () => {
        if (!editedCategoryName.trim() || !editedDescription.trim()) return;
    
        const payload = {
            assistant_id: assistantId,
            name: editedCategoryName,
            description: editedDescription,
        };
    
        try {
            if (isNew) {
                // Salva visualmente primeiro
                onSave({
                    name: editedCategoryName,
                    description: editedDescription,
                    id: "temp", // id temporário
                });

                setIsEditing(false);
                const response = await fetch(`${process.env.REACT_APP_API_URL}/post_category`, {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`
                    },
                    body: JSON.stringify(payload),
                });
    
                const data = await response.json();
    
                if (data.status && data.category_id) {
                    // Atualiza o último item com o novo ID real
                    setCategories((prev) => {
                        const updated = [...prev];
                        updated[updated.length - 1].id = data.category_id;
                        return updated;
                    });
                } else {
                    console.error("Erro ao salvar nova categoria.");
                }
            } else {
                setIsEditing(false);
                const response = await fetch(`${process.env.REACT_APP_API_URL}/put_category`, {
                    method: "PUT",
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`
                    },
                    body: JSON.stringify({
                        ...payload,
                        category_id: categoryId,
                        position: String(categoryPosition ?? 1),
                    }),
                });
    
                const data = await response.json();
    
                if (data.status) {
                    // Sucesso, nenhuma ação adicional necessária.
                } else {
                    console.error("Erro ao atualizar categoria existente.");
                }
            }
        } catch (error) {
            console.error("Erro ao comunicar com o servidor:", error);
        }
    };

    const handleCancelClick = async () => {
        if (isNew) {
            onCancel();
        } else {
            try {
                setIsRestoringPreviousDescription(true);

                const response = await fetch(`${process.env.REACT_APP_API_URL}/post_previous_description`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`
                    },
                    body: JSON.stringify({
                        assistant_id: assistantId,
                        category_id: categoryId,
                    }),
                });

                if (response.ok) {
                    const data = await response.json();
                    if (data?.previous_description) {
                        setEditedDescription(data.previous_description);
                    } else {
                        setEditedDescription(categoryDescription);
                    }
                } else {
                    setEditedDescription(categoryDescription);
                }
            } catch (error) {
                console.error('Erro ao buscar descrição anterior:', error);
                setEditedDescription(categoryDescription);
            } finally {
                setEditedCategoryName(categoryName);
                setIsRestoringPreviousDescription(false);
            }
        }
    };

    useEffect(() => {
        if (isEditing && textareaRef.current) {
            textareaRef.current.style.height = 'auto'; // reseta antes
            const scrollHeight = textareaRef.current.scrollHeight;
            textareaRef.current.style.height = `${Math.min(scrollHeight, 277.5)}px`;
        }
    }, [editedDescription, isEditing]);

    useEffect(() => {
        setEditedCategoryName(categoryName);
        setEditedDescription(categoryDescription);
    }, [categoryName, categoryDescription]);

    return (
        <div className={`category-item`}>
            {showUp && (
                <button
                    type="button"
                    className="category-reorder-button category-reorder-up"
                    onClick={onMoveUp}
                    aria-label="Mover para cima"
                >
                    <img src="/imagens/up-blue.svg" alt="Icon to scroll up" />
                </button>
            )}
            <div className='category-name-container'>
                {isEditing ? (
                    <>
                    {/* <img src='./imagens/black-pencil.svg' alt={tCommon('edit')} className="pencil-icon" /> */}

                    <input
                        className="category-name-input"
                        value={editedCategoryName}
                        onChange={(e) => setEditedCategoryName(e.target.value)}
                        size={Math.max(editedCategoryName.length, t('enterCategoryName').length)}
                        placeholder={tCommon('title')}
                    />

                    <img
                        src='./imagens/new-red-trash.svg'
                        alt={tCommon('delete')}
                        className="delete-icon"
                        onClick={onDelete}
                    />
                    </>
                ) : (
                    <p className='category-name'>{editedCategoryName}</p>
                )}
            </div>

            {isEditing && isNew === false && (
                <button 
                    className='btn-return-category-item'
                    onClick={handleCancelClick} 
                    disabled={isRestoringPreviousDescription}
                >
                <p className='text-return-category'>Retornar ao anterior</p>
                <img 
                    src='./imagens/new-return.svg' 
                    alt={tCommon('cancelEdit')} 
                    className="return-icon" 
                    
                />
                </button>
            )}

            <div className={`category-description ${isEditing ? 'editing' : ''}`}>
                {isEditing ? (
                    <textarea
                        className="category-input"
                        ref={textareaRef}
                        value={editedDescription}
                        onChange={(e) => setEditedDescription(e.target.value)}
                        placeholder={tCommon('enterCategoryDescription')}
                    />
                ) : (
                    <p>{editedDescription}</p>
                )}
                {!isEditing && (
                    <EditButton onClick={() => setIsEditing(true)} />
                )}
            </div>

            {isEditing && (
                <button 
                    className="save-button" 
                    onClick={handleSaveClick}
                    disabled={!editedCategoryName.trim() || !editedDescription.trim() || isRestoringPreviousDescription}
                >
                    {tCommon('save')}
                </button>
            )}
            
            {showDown && (
                <button
                    type="button"
                    className="category-reorder-button category-reorder-down"
                    onClick={onMoveDown}
                    aria-label="Mover para baixo"
                >
                    <img src="/imagens/down-blue.svg" alt="Icon to scroll down" />
                </button>
            )}
        </div>
    );
}

export default CategoryItem;
